import { Check } from 'lucide-react';
import { PaginatedSelectItemVariants } from './paginated-select.variants';
import { usePaginatedSelectContext } from './usePaginatedSelectContext';
import { cn } from '@/shared/lib/cn';

function PaginatedSelectItem({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & {
  item: Record<string, unknown>;
}) {
  const ctx = usePaginatedSelectContext();
  const {
    onValueChange,
    disabled: contextDisabled,
    isSelected,
  } = ctx as {
    onValueChange: (value: string) => void;
    disabled: boolean;
    isSelected: (value: string) => boolean;
  };

  const renderItem = (
    ctx as {
      renderItem?: (item: Record<string, unknown>) => {
        value: string;
        label: string;
        disabled?: boolean;
      };
    }
  ).renderItem;
  const item = props.item;
  const renderedItem = renderItem
    ? renderItem(item)
    : {
        value: String(item.id || ''),
        label: String(item.name || item.label || ''),
        disabled: false,
      };

  const itemIsSelected = isSelected(renderedItem.value);

  const handleSelect = () => {
    if (!contextDisabled && !renderedItem.disabled) {
      onValueChange(renderedItem.value);
    }
  };

  return (
    // react-doctor-disable-next-line react-doctor/prefer-tag-over-role -- no native <option> works inside this custom listbox; already keyboard-operable via tabIndex + Enter/Space
    <div
      role="option"
      tabIndex={0}
      aria-selected={itemIsSelected}
      data-state={itemIsSelected ? 'checked' : 'unchecked'}
      onClick={handleSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleSelect();
        }
      }}
      className={cn(
        PaginatedSelectItemVariants({
          selected: itemIsSelected,
          disabled: contextDisabled || renderedItem.disabled,
          class: className,
        }),
        'group cursor-pointer',
      )}
      {...props}
    >
      <div className="flex items-center gap-2 flex-1">
        <div
          className={cn(
            'flex items-center justify-center w-4 h-4 border rounded transition-colors',
            itemIsSelected
              ? 'bg-primary border-primary'
              : 'border-muted-foreground/30 group-hover:border-primary/50',
          )}
        >
          {itemIsSelected && <Check className="w-3 h-3 text-primary-foreground" />}
        </div>
        <span className="truncate">{renderedItem.label}</span>
      </div>
    </div>
  );
}

export { PaginatedSelectItem };
